import { Calendar, Clock, ChevronLeft, ChevronRight, Scissors, User } from "lucide-react";
import { Button } from "../ui/Button";
import GlobalLoader from "../ui/GlobalLoader";
import { UpcomingBooking } from "../../services/dashboard";

interface UpcomingBookingsProps {
  currencySymbol?: string;
  bookings: UpcomingBooking[];
  loading: boolean;
  selectedDate: Date;
  onDateChange: (date: Date) => void;
  onBookingClick?: (bookingId: string | number) => void;
}

const UpcomingBookings: React.FC<UpcomingBookingsProps> = ({
  currencySymbol = "",
  bookings,
  loading,
  selectedDate,
  onDateChange,
  onBookingClick,
}) => {
  const daysToShow = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() - selectedDate.getDay() + i);
    return d;
  });

  const isToday = (day: Date) => day.toDateString() === new Date().toDateString();

  return (
    <div className="flex-1 bg-white rounded-2xl shadow-lg p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-[#1E1E1E]">Upcoming Bookings</h2>
        <button
          type="button"
          onClick={() => onDateChange(new Date())}
          className="text-xs font-semibold text-[#885ABB] hover:underline"
        >
          Today
        </button>
      </div>

      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-1 sm:gap-2">
          <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-[#885ABB]" />
          <p className="text-[#885ABB] font-bold text-base sm:text-lg">
            {selectedDate.toLocaleString("default", {
              month: "long",
              year: "numeric",
            })}
          </p>
        </div>

        <div className="flex">
          <Button
            onClick={() => onDateChange(new Date(selectedDate.getTime() - 7 * 86400000))}
            className="text-[#885ABB] transition-colors p-2"
          >
            <ChevronLeft className="w-8 h-8" />
          </Button>
          <Button
            onClick={() => onDateChange(new Date(selectedDate.getTime() + 7 * 86400000))}
            className="text-gray-400 hover:text-[#885ABB] transition-colors p-2"
          >
            <ChevronRight className="w-8 h-8" />
          </Button>
        </div>
      </div>

      <div className="flex gap-2 mb-5 overflow-x-auto pb-2 sm:pb-0 sm:overflow-visible scrollbar-hide">
        {daysToShow.map((day) => {
          const selected = day.toDateString() === selectedDate.toDateString();
          return (
            <button
              key={day.toDateString()}
              type="button"
              onClick={() => onDateChange(new Date(day))}
              className={`flex-none w-12 sm:flex-1 flex flex-col items-center justify-center py-3 px-1 sm:px-2 rounded-xl text-sm font-semibold transition-all duration-200 ${
                selected
                  ? "bg-[#885ABB] text-white shadow-md scale-105"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              <span className="text-base mb-1">{day.getDate()}</span>
              <span className="text-[10px] sm:text-xs">
                {isToday(day) ? "Today" : day.toLocaleDateString("en-US", { weekday: "short" })}
              </span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <GlobalLoader fullScreen={false} description="Updating bookings..." compensateSidebar={false} />
        </div>
      ) : bookings.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-gray-400 text-sm">No bookings for this date.</p>
        </div>
      ) : (
        <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
          {bookings.map((booking) => (
            <button
              key={booking.id}
              type="button"
              onClick={() => onBookingClick?.(booking.id)}
              className="w-full text-left bg-[#EDE3F7] p-4 rounded-xl hover:shadow-md transition-all duration-200 border-l-4 border-[#885ABB]"
            >
              <div className="flex justify-between items-start mb-2">
                <p className="font-bold text-[#1E1E1E] text-base tracking-wide">
                  {booking.customer}
                </p>
                <p className="font-bold text-[#1E1E1E] text-lg">
                  {currencySymbol}{booking.amount.toFixed(2)}
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-3 mb-2">
                <div className="flex items-center gap-1 text-gray-700 text-sm">
                  <Clock className="w-4 h-4 text-[#885ABB]" />
                  <span>{booking.time}</span>
                </div>
                <div className="flex items-center gap-1 text-gray-700 text-sm">
                  <User className="w-4 h-4 text-[#885ABB]" />
                  <span>{booking.staff ?? "Unassigned"}</span>
                </div>
              </div>

              <p className="text-sm font-medium text-[#402B69] flex items-center gap-1.5">
                <Scissors className="w-4 h-4 text-[#885ABB]" />
                {booking.services_label ?? "—"}
              </p>
            </button>
          ))}
        </div>
      )}

      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #885ABB; border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #6B4799; }
      `}</style>
    </div>
  );
};

export default UpcomingBookings;
